import { createSelector } from "@reduxjs/toolkit";
import { FaroCoreApiUtils } from "@stellar/core-api-client";
import { membersAdapter } from "@store/members/members-slice";
import { getMembersByKey } from "@utils/invite-xg-seat-utils";
import { hasXgSeatPermission } from "@utils/permission-utils";
import { getPrettyName } from "@utils/user-utils";

/**
 * Returns all the stored members. It filter out the teams and only return the members
 */
export const companyMembersSelector = createSelector(state => state.members, state => {
  return membersAdapter.getSelectors().selectAll(state).filter(member => member.kind === "user");
});

/**
 * Returns how many workspace members hold a Sphere XG seat that was assigned to them, as far as the
 * members list tells, or undefined while the workspace members are not fetched, since the stored
 * members may then be only some of them.
 */
export const xgSeatHoldersCountSelector = createSelector(companyMembersSelector, state => state.members.fetched.hasFetchedWorkspaceMembers, (members, hasFetchedWorkspaceMembers) => {
  if (!hasFetchedWorkspaceMembers) {
    return undefined;
  }
  return members.filter(member => hasXgSeatPermission({
    member
  })).length;
});

/**
 * Returns the stored members by their identity and their lower-cased email, built once per change
 * of the members, so that members can be looked up from many rows at once
 */
export const companyMembersByKeySelector = createSelector(companyMembersSelector, getMembersByKey);

/**
 * Returns all the stored teams as member type. It filter out the members and only return the teams
 */
export const companyTeamsAsMemberSelector = createSelector(state => state.members, members => {
  return membersAdapter.getSelectors().selectAll(members).filter(FaroCoreApiUtils.Member.isTeam);
});

/**
 * Returns all the stored members filtered by search text.
 */
export const filteredCompanyMembersSelector = createSelector(state => state.ui.search.debouncedSearchText, companyMembersSelector, (searchText, members) => members.filter(member => isMemberMatchingSearch(member, searchText)));

/**
 * Whether the member's email or name contains the search text, ignoring case.
 * The pretty name is used in case the user types the full name in the search bar.
 */
export function isMemberMatchingSearch(member, searchText) {
  const searchTextLower = searchText.toLowerCase();
  return member.email.toLowerCase().includes(searchTextLower) || getPrettyName(member).toLowerCase().includes(searchTextLower);
}

/**
 * Returns the fetching properties of the members slice.
 */
export const fetchingMembersFlagsSelector = createSelector(state => state.members, members => {
  return members.fetching;
});

/**
 * Returns the fetched properties of the members slice.
 */
export const fetchedMembersFlagsSelector = createSelector(state => state.members, members => {
  return members.fetched;
});

/**
 * Returns whether the workspace members are not available yet, either because they have not been
 * requested yet or because the request is still running.
 *
 * Everything that is shown from the workspace members has to treat both cases as loading.
 * Otherwise an action would already be offered on the empty state that is held until the members
 * arrive.
 */
export const areCompanyMembersLoadingSelector = createSelector(fetchingMembersFlagsSelector, fetchedMembersFlagsSelector, ({
  isFetchingCompanyMembers,
  hasFetchingCompanyMembersFailed
}, {
  hasFetchedWorkspaceMembers
}) => {
  return isFetchingCompanyMembers || !hasFetchedWorkspaceMembers && !hasFetchingCompanyMembersFailed;
});

/**
 * Get company member with the provided role
 */
export function companyMembersByRoleSelector(userCompanyRole) {
  return createSelector(companyMembersSelector, members => {
    return members.filter(({
      role
    }) => role === userCompanyRole);
  });
}

/**
 * Get single member with the provided member id.
 * The id is not the actual member id, but the id used to store the member in the store,
 * which is either identity or email.
 *
 * @see getMemberId
 */
export function companyMemberSelector(memberId) {
  return createSelector(state => state.members, members => {
    if (!memberId) {
      return null;
    }
    return membersAdapter.getSelectors().selectById(members, memberId) ?? null;
  });
}